import {
    getPrescriptionDetailsLazy,
    getRxEventHistoryLazy,
} from '@/hooks/rx/queries/usePrescriptionDetailsQuery';
import { useQueryParams } from '@/hooks/shared/useQueryParams';
import { useSidenavOpenState } from '@/hooks/shared/useSidenavOpenState';
import { RxEvent } from '@/types/rx/prescriptionDetails';
import { CustomUser } from '@/types/shared/user';
import { formatUtcToLocalDate } from '@/utils/format/formatUtcToLocalDate';
import { formatUtcToLocalTime } from '@/utils/format/formatUtcToLocalTime';
import { useAuth0 } from '@auth0/auth0-react';
import { CodeHighlight } from '@mantine/code-highlight';
import { Group, Kbd, Loader, Modal, Stack, Table, Tabs, Text, Timeline } from '@mantine/core';
import { useHotkeys } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Spotlight, SpotlightActionData, SpotlightActionGroupData } from '@mantine/spotlight';
import {
    IconClipboard,
    IconHelp,
    IconHistory,
    IconPrescription,
    IconSearch,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { ReactNode, useState } from 'react';

interface KeyboardShortcutProviderProps {
    children: ReactNode;
}

export default function KeyboardShortcutProvider({ children }: KeyboardShortcutProviderProps) {
    const { getAccessTokenSilently, user } = useAuth0<CustomUser>();
    const queryClient = useQueryClient();
    const router = useRouter();

    // For keyboard shortcuts
    const { practiceUuid, prescriptionUuid, practiceId, corpGroupId } = useQueryParams();
    const [_, setDesktopOpened] = useSidenavOpenState();

    // For Spotlight functionality
    const [spotlightQuery, setSpotlightQuery] = useState<string>('');

    // For showing Rx event history
    const [rxEventsLoading, setRxEventsLoading] = useState<boolean>(false);
    const [rxEventsToShow, setRxEventsToShow] = useState<RxEvent[]>([]);

    // Define which keyboard shortcuts to support, and their handlers
    useHotkeys([
        ['mod+alt+H', handleHelp],
        ['mod+alt+R', handleCopyRxUuid],
        ['mod+alt+P', handleCopyPracticeUuid],
        ['mod+alt+E', handleCopyEntityId],
        ['mod+alt+A', handleCopyAccessToken],
        ['mod+alt+L', handleViewRxEventLog],
        ['mod+B', handleToggleDesktopSidenav],
    ]);

    // Show a toast explaining the available keyboard shortcuts
    function handleHelp() {
        notifications.show({
            title: (
                <Text size="sm" pt="md" fw={500}>
                    Available keyboard shortcuts
                </Text>
            ),
            message: (
                <Stack gap={0} my="md">
                    <Text size="sm">
                        <Kbd>mod</Kbd> + <Kbd>K</Kbd> - Open the spotlight search bar
                    </Text>
                    <Text size="sm">
                        <Kbd>mod</Kbd> + <Kbd>alt</Kbd> + <Kbd>H</Kbd> - Show this help dialog
                    </Text>
                    <Text size="sm">
                        <Kbd>mod</Kbd> + <Kbd>alt</Kbd> + <Kbd>R</Kbd> - Copy Rx UUID to clipboard
                    </Text>
                    <Text size="sm">
                        <Kbd>mod</Kbd> + <Kbd>alt</Kbd> + <Kbd>P</Kbd> - Copy practice UUID to
                        clipboard
                    </Text>
                    <Text size="sm">
                        <Kbd>mod</Kbd> + <Kbd>alt</Kbd> + <Kbd>E</Kbd> - Copy entity ID to clipboard
                    </Text>
                    <Text size="sm">
                        <Kbd>mod</Kbd> + <Kbd>alt</Kbd> + <Kbd>A</Kbd> - Copy access token to
                        clipboard
                    </Text>
                    <Text size="sm">
                        <Kbd>mod</Kbd> + <Kbd>alt</Kbd> + <Kbd>L</Kbd> - View Rx event log
                    </Text>
                    <Text size="sm">
                        <Kbd>mod</Kbd> + <Kbd>B</Kbd> - Toggle desktop left nav
                    </Text>
                </Stack>
            ),
            withCloseButton: true,
            autoClose: false,
            color: 'gray',
            withBorder: true,
        });
    }

    // Copy Rx UUID from URL to clipboard
    async function handleCopyRxUuid() {
        if (!prescriptionUuid) {
            showWarning('Failed to copy Rx UUID - no Rx UUID found in URL');
            return;
        }

        try {
            await navigator.clipboard.writeText(prescriptionUuid);

            showSuccessMessage('Copied Rx UUID to clipboard');
        } catch (e) {
            showWarning(`Failed to copy Rx UUID to clipboard: ${e}`);
        }
    }

    // Copy practice UUID from URL to clipboard
    async function handleCopyPracticeUuid() {
        if (!practiceUuid) {
            showWarning('Failed to copy practice UUID - no practice UUID found in URL');
            return;
        }

        try {
            await navigator.clipboard.writeText(practiceUuid);

            showSuccessMessage('Copied practice UUID to clipboard');
        } catch (e) {
            showWarning(`Failed to copy practice UUID to clipboard: ${e}`);
        }
    }

    // Copy entity ID from URL to clipboard (either practice or corporate group)
    async function handleCopyEntityId() {
        if (!practiceId && !corpGroupId) {
            showWarning('Failed to copy entity ID - no entity ID found in URL');
            return;
        }

        if (practiceId) {
            try {
                await navigator.clipboard.writeText(practiceId);

                showSuccessMessage('Copied practice entity ID to clipboard');
            } catch (e) {
                showWarning(`Failed to copy practice entity ID to clipboard: ${e}`);
            }
        } else if (corpGroupId) {
            try {
                await navigator.clipboard.writeText(corpGroupId);

                showSuccessMessage('Copied corporate group entity ID to clipboard');
            } catch (e) {
                showWarning(`Failed to copy corporate group entity ID to clipboard: ${e}`);
            }
        }
    }

    // Copy access token from URL to clipboard
    async function handleCopyAccessToken() {
        const accessToken = await getAccessTokenSilently();

        if (!accessToken) {
            showWarning('Failed to copy access token, please refresh and try again');
            return;
        }

        try {
            await navigator.clipboard.writeText(accessToken);

            showSuccessMessage('Copied access token to clipboard');
        } catch (e) {
            showWarning(`Failed to copy access token to clipboard: ${e}`);
        }
    }

    function handleToggleDesktopSidenav() {
        setDesktopOpened((prev) => !prev);
    }

    // Show a yellow warning toast
    function showWarning(message: string) {
        notifications.show({
            message,
            autoClose: 5000,
            withCloseButton: true,
            color: 'yellow',
            withBorder: true,
        });
    }

    // Show a green success toast
    function showSuccessMessage(message: string) {
        notifications.show({
            message,
            autoClose: 5000,
            withCloseButton: true,
            color: 'green',
            withBorder: true,
        });
    }

    // Get practice UUID and redirect user to appropriate page
    async function handleRxDetailsShortcut() {
        if (!spotlightQuery || !spotlightQuery.includes('rx_')) {
            showWarning('Failed to open Rx details - no Rx UUID provided');
            return;
        }

        try {
            const accessToken = await getAccessTokenSilently();

            const prescriptionDetails = await getPrescriptionDetailsLazy({
                accessToken,
                prescriptionUuid: spotlightQuery,
                queryClient,
            });

            // Redirect user to Rx details page
            router.push(`/rx/${prescriptionDetails.practice_uuid}/prescriptions/${spotlightQuery}`);
        } catch (e) {
            showWarning(`Error fetching Rx details: ${e}`);
        }
    }

    async function handleViewRxEventLog() {
        if (!prescriptionUuid) {
            showWarning('Failed to get Rx event history - no Rx UUID found in URL');
            return;
        }

        try {
            setRxEventsLoading(true);
            const accessToken = await getAccessTokenSilently();

            const rxEventHistory = await getRxEventHistoryLazy(accessToken, prescriptionUuid);

            setRxEventsToShow(rxEventHistory);
        } catch (e) {
            showWarning(`Error fetching Rx event history: ${e}`);
        } finally {
            setRxEventsLoading(false);
        }
    }

    // Define possible Spotlight actions (employees only)
    const actions: (SpotlightActionGroupData | SpotlightActionData)[] = [
        {
            group: 'Navigation',
            actions: [
                {
                    id: 'rx-details-shortcut',
                    label: 'Rx Details',
                    title: 'Rx Details',
                    keywords: ['rx_', spotlightQuery.includes('rx_') ? spotlightQuery : ''],
                    description: 'Paste rx_uuid to open Rx details page',
                    onClick: handleRxDetailsShortcut,
                    leftSection: <IconPrescription size={20} stroke={1.5} />,
                },
            ],
        },
        {
            group: 'Copy to clipboard',
            actions: [
                {
                    id: 'copy-rx-uuid',
                    label: 'Copy Rx UUID',
                    title: 'Copy Rx UUID',
                    keywords: ['copy', 'rx', 'uuid', 'rx_uuid'],
                    description: 'Copy Rx UUID to clipboard',
                    onClick: handleCopyRxUuid,
                    leftSection: <IconClipboard size={20} stroke={1.5} />,
                },
                {
                    id: 'copy-practice-uuid',
                    label: 'Copy Practice UUID',
                    title: 'Copy Practice UUID',
                    keywords: ['copy', 'practice', 'uuid', 'practice_uuid'],
                    description: 'Copy Practice UUID to clipboard',
                    onClick: handleCopyPracticeUuid,
                    leftSection: <IconClipboard size={20} stroke={1.5} />,
                },
                {
                    id: 'copy-entity-id',
                    label: 'Copy Entity ID',
                    title: 'Copy Entity ID',
                    keywords: ['copy', 'entity', 'id', 'entity_id'],
                    description: 'Copy entity ID to clipboard',
                    onClick: handleCopyEntityId,
                    leftSection: <IconClipboard size={20} stroke={1.5} />,
                },
                {
                    id: 'copy-access-token',
                    label: 'Copy Access Token',
                    title: 'Copy Access Token',
                    keywords: ['copy', 'access', 'token', 'access_token'],
                    description: 'Copy access token to clipboard',
                    onClick: handleCopyAccessToken,
                    leftSection: <IconClipboard size={20} stroke={1.5} />,
                },
            ],
        },
        {
            group: 'Debug',
            actions: [
                {
                    id: 'rx-event-log',
                    label: 'View Rx Event Log',
                    title: 'View Rx Event Log',
                    keywords: ['view', 'rx', 'event', 'log'],
                    description: 'View Rx event log - only applicable on Rx Details page',
                    onClick: handleViewRxEventLog,
                    leftSection: <IconHistory size={20} stroke={1.5} />,
                },
            ],
        },
        {
            group: 'Help',
            actions: [
                {
                    id: 'help',
                    label: 'Help',
                    title: 'Help',
                    keywords: ['help', 'keyboard shortcuts'],
                    description: 'Show the help dialog (available keyboard shortcuts)',
                    onClick: handleHelp,
                    leftSection: <IconHelp size={20} stroke={1.5} />,
                },
            ],
        },
    ];

    return (
        <>
            {children}

            {user?.is_employee && (
                <>
                    <Spotlight
                        query={spotlightQuery}
                        onQueryChange={setSpotlightQuery}
                        actions={actions}
                        nothingFound="Nothing found..."
                        searchProps={{
                            leftSection: <IconSearch size={20} stroke={1.5} />,
                            placeholder: 'Search...',
                        }}
                    />

                    <Modal
                        opened={rxEventsLoading || rxEventsToShow.length > 0}
                        size="xl"
                        onClose={() => {
                            setRxEventsLoading(false);
                            setRxEventsToShow([]);
                        }}
                        title="Rx Event Log"
                        centered
                    >
                        {rxEventsLoading ? (
                            <Group>
                                {/* Explicit color: this Rx-branded modal can open over a reach
                                    page, where the primary color is remapped to dvm-blue. */}
                                <Loader size="xs" color="rx-purple" />
                                <Text>Loading Rx events...</Text>
                            </Group>
                        ) : (
                            <Tabs defaultValue="overview">
                                {/* Tabs to click */}
                                <Tabs.List>
                                    <Tabs.Tab value="overview">Overview</Tabs.Tab>
                                    <Tabs.Tab value="details">Details</Tabs.Tab>
                                </Tabs.List>

                                {/* Overview tab */}
                                <Tabs.Panel value="overview" py="md">
                                    <Table highlightOnHover withRowBorders>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Timestamp</Table.Th>
                                                <Table.Th>Event type</Table.Th>
                                                <Table.Th>Event source</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>

                                        <Table.Tbody>
                                            {rxEventsToShow.map((event) => (
                                                <Table.Tr key={event.id}>
                                                    <Table.Td>
                                                        {formatUtcToLocalDate(event.timestamp)} at{' '}
                                                        {formatUtcToLocalTime(
                                                            event.timestamp,
                                                            true,
                                                        )}
                                                    </Table.Td>
                                                    <Table.Td>{event.type}</Table.Td>
                                                    <Table.Td>{event.source}</Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </Tabs.Panel>

                                {/* Details tab */}
                                <Tabs.Panel value="details" py="md">
                                    <Timeline color="rx-purple" active={rxEventsToShow.length}>
                                        {rxEventsToShow.map((event) => (
                                            <Timeline.Item key={event.id} title={event.type}>
                                                {/* Timestamp and source */}
                                                <Text size="sm" c="dimmed">
                                                    {formatUtcToLocalDate(event.timestamp)} at{' '}
                                                    {formatUtcToLocalTime(event.timestamp, true)} |{' '}
                                                    {event.source}
                                                </Text>

                                                <CodeHighlight
                                                    code={JSON.stringify(event.body, null, 4)}
                                                    language="json"
                                                    withCopyButton={false}
                                                    styles={{
                                                        code: {
                                                            fontSize: 'var(--mantine-font-size-xs)',
                                                        },
                                                    }}
                                                />
                                            </Timeline.Item>
                                        ))}
                                    </Timeline>
                                </Tabs.Panel>
                            </Tabs>
                        )}
                    </Modal>
                </>
            )}
        </>
    );
}
